import { t, Trans } from '@lingui/macro';
import _ from 'lodash';
import { useFormContext } from 'react-hook-form';
import FormSection from '../../form/FormSection';
import { COLUMN_TYPES } from '../../data-table/DataTableColumns';
import { LegacyIcon } from '../../icon';
import {
    AGENT_MODEL_SHORT_DISPLAY_FIELD,
    AGENT_NAME_FIELD,
    AGENT_NETWORK_HOSTNAME_FIELD,
    AGENT_NETWORK_IPV4_EXTERNAL_FIELD,
    AGENT_NETWORK_IPV4_EXTERNAL_SORT_FIELD,
    AGENT_NETWORK_IPV4_LOCAL_FIELD,
    AGENT_NETWORK_MAC_FIELD,
    AGENT_STATUS_FIELD,
    AGENT_TAGS_DISPLAY_FIELD,
    AGENT_TRANSLATED_TYPE_FIELD,
    AGENT_TYPE_FIELD,
    AGENT_TYPE_ICON_FIELD,
    AGENT_WIFI_SSID_FIELD,
} from '../agents/AgentConstants';
import { renderStatus } from '../agents/AgentRenderHelper';
import ListSelectorField from '../../form/ListSelectorField';
import { SORT_ASC } from '../../../utilities/FormUtilities';
import { AGENTS_FIELD, IP_RULES_FIELD, MATCHING_AGENTS_DATA_SOURCE } from './SiteAdminConstants';
import { useColumnStyles } from '../agents/AgentStyles';

function buildAgentColumns(classes) {
    return [
        {
            type: COLUMN_TYPES.TEXT,
            title: t`Name`,
            sortable: true,
            mapping: AGENT_NAME_FIELD,
            width: 250,
        },
        {
            type: COLUMN_TYPES.TEXT,
            title: t`Model`,
            sortable: true,
            mapping: AGENT_MODEL_SHORT_DISPLAY_FIELD,
            width: 85,
        },
        {
            type: COLUMN_TYPES.TEXT,
            title: t`Status`,
            sortable: true,
            mapping: AGENT_STATUS_FIELD,
            width: 105,
            className: classes.root,
            renderer: renderStatus,
        },
        {
            type: COLUMN_TYPES.TEXT,
            title: t`Admin MAC`,
            sortable: true,
            mapping: AGENT_NETWORK_MAC_FIELD,
            width: 130,
        },
        {
            type: COLUMN_TYPES.TEXT,
            title: t`Internal IP`,
            sortable: true,
            mapping: AGENT_NETWORK_IPV4_LOCAL_FIELD,
            width: 140,
        },
        {
            type: COLUMN_TYPES.TEXT,
            title: t`External IP`,
            sortable: true,
            mapping: AGENT_NETWORK_IPV4_EXTERNAL_FIELD,
            sortProperty: AGENT_NETWORK_IPV4_EXTERNAL_SORT_FIELD,
            width: 140,
        },
    ];
}

const agentSelectedColumns = [
    {
        type: COLUMN_TYPES.TEXT,
        title: t`Associated`,
        mapper: (row) => (row?.rule?.valueDisplay ? associatedByScopeText : associatedManuallyText),
        getTooltip: (row) => (row?.rule?.valueDisplay ? associatedByScopeText : associatedManuallyText),
        sortable: false, // API limitation - cannot sort on this column! See: DE29024
    },
    {
        type: COLUMN_TYPES.TEXT,
        title: t`IP Scope`,
        mapping: 'rule.valueDisplay',
        getTooltip: (row) => row?.rule?.valueDisplay,
        sortable: false, // API limitation - cannot sort on this column! See: DE29024
    },
];

const selectionDataSource = {
    totalProperty: 'count',
    dataProperty: 'data',
    params: {
        searchFields: [
            AGENT_NAME_FIELD,
            AGENT_STATUS_FIELD,
            AGENT_TAGS_DISPLAY_FIELD,
            AGENT_MODEL_SHORT_DISPLAY_FIELD,
            AGENT_WIFI_SSID_FIELD,
            AGENT_NETWORK_HOSTNAME_FIELD,
        ],
    },
    initialSort: [{ property: AGENT_NAME_FIELD, direction: SORT_ASC }],
};

const availableDataSource = {
    ...selectionDataSource,
    url: '/query/admin/agents/findAvailable',
};

const idsDataSource = {
    params: selectionDataSource.params,
    url: '/query/admin/agents/selectionUUIDs',
};

const initialSort = [{ property: AGENT_NAME_FIELD, direction: SORT_ASC }];

const associatedManuallyText = t`Manually`;
const associatedByScopeText = t`By IP Scope`;
const addAgentButton = t`Add nPoints`;
const emptyAgentsGridText = t`There are no nPoints in this site`;

const agentTypeColumn = {
    type: COLUMN_TYPES.ICON,
    title: t`Type`,
    width: 74,
    mapping: AGENT_TYPE_ICON_FIELD,
    sortable: true,
    csvFormatter: (value, row) => row[AGENT_TRANSLATED_TYPE_FIELD],
    icons: [
        {
            getIcon: (row) => <LegacyIcon icon={row[AGENT_TYPE_ICON_FIELD]} />,
            getTooltip: (row) => row[AGENT_TRANSLATED_TYPE_FIELD],
        },
    ],
};

const selectedAgentTypeColumn = _.extend({}, _.cloneDeep(agentTypeColumn), {
    sortProperty: AGENT_TYPE_FIELD,
});

export function SiteAdminAgents({ formData, setAgentSelectionIsDirty }) {
    const classes = useColumnStyles();
    const baseColumns = buildAgentColumns(classes);
    const columns = [agentTypeColumn, ...baseColumns];
    const selectedColumns = [selectedAgentTypeColumn, ...baseColumns, ...agentSelectedColumns];
    const { watch } = useFormContext();
    const rules = watch(IP_RULES_FIELD);
    const selectedDataSource = {
        ...MATCHING_AGENTS_DATA_SOURCE,
        body: { agentRules: rules },
        params: {
            ...MATCHING_AGENTS_DATA_SOURCE.params,
        },
    };

    return (
        <FormSection title={<Trans>nPoints</Trans>} addSpacer>
            <ListSelectorField
                gridColumns={columns}
                selectedGridColumns={selectedColumns}
                sorters={initialSort}
                dbSelected={formData[AGENTS_FIELD]}
                selectedDataSource={selectedDataSource}
                availableDataSource={availableDataSource}
                idsDataSource={idsDataSource}
                fieldName={AGENTS_FIELD}
                addButtonText={addAgentButton}
                setSelectionIsDirty={setAgentSelectionIsDirty}
                isMultiRemove={false}
                selectedEmptyGridText={emptyAgentsGridText}
            />
        </FormSection>
    );
}
